import { useCallback, useEffect, useState } from "react";
import type { WebMoney } from "../contests/data";
import { asTier, type Tier } from "../tiers";

// Mirrors core.WebContestDetail (GET /v1/web/contests/{idOrSlug}). The page
// switches on `state` and `viewer` as sent and never re-derives either;
// money arrives as cents plus a display string.
export type ContestState = "draft" | "open" | "closed" | "judging" | "complete" | "cancelled";

export type Person = { username: string; logo_url?: string };

export type ContestViewer = {
  logged_in: boolean;
  email_verified: boolean;
  is_entrant: boolean;
  has_reservation: boolean;
  has_submitted: boolean;
  is_sponsor: boolean;
  is_admin: boolean;
  can_reserve: boolean;
  can_submit: boolean;
  can_judge: boolean;
};

export type ContestDetail = {
  id: string;
  slug: string;
  title: string;
  description?: string;
  tags: string[];
  challenge?: string;
  genre?: string;
  duration?: string;
  format: "standard" | "versus";
  state: ContestState;
  show_countdown: boolean;
  ends_at: string;
  seats_total: number;
  seats_taken: number;
  views: number;
  cover_url?: string;
  prize: WebMoney;
  admission: WebMoney;
  is_sponsorship: boolean;
  is_seeded: boolean;
  has_sample_pack: boolean;
  late_message?: string;
  cancellation_reason?: string;
  sponsor?: Person;
  winners: (Person & { placement: number })[];
  viewer: ContestViewer;
};

export type ActivityItem = { kind: "entry" | "submission" | "review" | "result"; actor: string; detail: string; at: string };

export type Review = {
  track_title?: string;
  producer_username?: string;
  reviewer_username: string;
  reviewer_logo_url?: string;
  rating: number;
  body?: string;
  video_url?: string;
  audio_url?: string;
  at: string;
};

// Mirrors core.WebMarketRead (GET /v1/web/contests/{idOrSlug}/market).
export type MarketOutcome = {
  producer_id: string;
  username: string;
  logo_url?: string;
  staked_cents: number;
  share_bps: number;
  american_odds: string;
  backer_count: number;
};

export type MarketAmount = { producer_id?: string; amount_cents: number; amount_display: string; paid: boolean };

export type BackReason = "anonymous" | "closed" | "unverified" | "entrant";

export type MarketViewer = {
  can_back: boolean;
  reason?: BackReason;
  balance_cents?: number;
  balance_display?: string;
  stake_cents: number;
  picks: MarketAmount[];
  payout?: MarketAmount;
  refund?: MarketAmount;
  // The viewer's own running backing share while picks are live. An
  // estimate from the stakes in hand, absent once the market settles.
  backing?: { earned_cents: number; earned_display: string; backers: number };
};

export type MarketSettlement = {
  pool_display: string;
  rake_display: string;
  producer_cut_display: string;
  winner_producer_id?: string;
  winner_username?: string;
};

export type Market = {
  market_id: string;
  status: "open" | "locked" | "settled" | "voided";
  // The market's tier, absent on a read that did not name one.
  tier?: Tier;
  pool_cents: number;
  pool_display: string;
  take_bps: number;
  rake_bps: number;
  producer_bps: number;
  backing_bps: number;
  fee_display: string;
  producer_cut_display: string;
  // Empty when the market pays no backing share.
  backing_display?: string;
  min_stake_display: string;
  per_user_cap_display: string;
  // The house seed added to the winners' split, and the house's share of
  // the pool; empty when there is none.
  bonus_display?: string;
  house_display?: string;
  outcomes: MarketOutcome[];
  settlement?: MarketSettlement;
  void_reason?: string;
  viewer: MarketViewer;
};


type Raw = Record<string, unknown>;

const obj = (v: unknown): Raw => (v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Raw) : {});
const str = (v: unknown): string | undefined => (typeof v === "string" && v !== "" ? v : undefined);
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const bool = (v: unknown): boolean => v === true;
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const STATES: readonly ContestState[] = ["draft", "open", "closed", "judging", "complete", "cancelled"];

function money(raw: unknown): WebMoney {
  const m = obj(raw);
  return {
    kind: (str(m.kind) ?? "none") as WebMoney["kind"],
    cents: typeof m.cents === "number" ? m.cents : undefined,
    display: str(m.display),
    take_bps: typeof m.take_bps === "number" ? m.take_bps : undefined,
    label: str(m.label),
  };
}

function person(raw: unknown): Person | undefined {
  const p = obj(raw);
  const username = str(p.username);
  return username ? { username, logo_url: str(p.logo_url) } : undefined;
}

export function normalizeViewer(raw: unknown): ContestViewer {
  const v = obj(raw);
  return {
    logged_in: bool(v.logged_in),
    email_verified: bool(v.email_verified),
    is_entrant: bool(v.is_entrant),
    has_reservation: bool(v.has_reservation),
    has_submitted: bool(v.has_submitted),
    is_sponsor: bool(v.is_sponsor),
    is_admin: bool(v.is_admin),
    can_reserve: bool(v.can_reserve),
    can_submit: bool(v.can_submit),
    can_judge: bool(v.can_judge),
  };
}

// Keep only the fields the page uses, so nothing extra in the payload can
// reach state or the DOM.
export function normalizeDetail(raw: unknown): ContestDetail | null {
  const c = obj(raw);
  const slug = str(c.slug);
  const id = str(c.id);
  if (!slug || !id) return null;
  const state = STATES.includes(c.state as ContestState) ? (c.state as ContestState) : "open";
  return {
    id,
    slug,
    title: str(c.title) ?? slug,
    description: str(c.description),
    tags: arr(c.tags).filter((t): t is string => typeof t === "string" && t !== ""),
    challenge: str(c.challenge),
    genre: str(c.genre),
    duration: str(c.duration),
    format: c.format === "versus" ? "versus" : "standard",
    state,
    show_countdown: bool(c.show_countdown),
    ends_at: str(c.ends_at) ?? "",
    seats_total: num(c.seats_total),
    seats_taken: num(c.seats_taken),
    views: num(c.views),
    cover_url: str(c.cover_url),
    prize: money(c.prize),
    admission: money(c.admission),
    is_sponsorship: bool(c.is_sponsorship),
    is_seeded: bool(c.is_seeded),
    has_sample_pack: bool(c.has_sample_pack),
    late_message: str(c.late_message),
    cancellation_reason: str(c.cancellation_reason),
    sponsor: person(c.sponsor),
    winners: arr(c.winners).flatMap((w) => {
      const p = person(w);
      return p ? [{ ...p, placement: num(obj(w).placement) }] : [];
    }),
    viewer: normalizeViewer(c.viewer),
  };
}

const KINDS: readonly ActivityItem["kind"][] = ["entry", "submission", "review", "result"];

export function normalizeActivity(raw: unknown): ActivityItem[] {
  return arr(raw).flatMap((r) => {
    const a = obj(r);
    const actor = str(a.actor);
    if (!actor || !KINDS.includes(a.kind as ActivityItem["kind"])) return [];
    return [{ kind: a.kind as ActivityItem["kind"], actor, detail: str(a.detail) ?? "", at: str(a.at) ?? "" }];
  });
}

// Media must be an absolute http(s) URL; the server filters too.
const httpURL = (v: unknown): string | undefined => {
  const s = str(v);
  return s && /^https?:\/\//i.test(s) ? s : undefined;
};

export function normalizeReviews(raw: unknown): Review[] {
  return arr(raw).flatMap((r) => {
    const v = obj(r);
    const reviewer = str(v.reviewer_username);
    if (!reviewer) return [];
    return [
      {
        track_title: str(v.track_title),
        producer_username: str(v.producer_username),
        reviewer_username: reviewer,
        reviewer_logo_url: httpURL(v.reviewer_logo_url),
        rating: Math.max(0, Math.min(5, Math.round(num(v.rating)))),
        body: str(v.body),
        video_url: httpURL(v.video_url),
        audio_url: httpURL(v.audio_url),
        at: str(v.at) ?? "",
      },
    ];
  });
}

function outcome(raw: unknown): MarketOutcome | null {
  const o = obj(raw);
  const id = str(o.producer_id);
  if (!id) return null;
  return {
    producer_id: id,
    username: str(o.username) ?? "",
    logo_url: str(o.logo_url),
    staked_cents: num(o.staked_cents),
    share_bps: num(o.share_bps),
    american_odds: str(o.american_odds) ?? "—",
    backer_count: num(o.backer_count),
  };
}

export const normalizeOutcomes = (raw: unknown): MarketOutcome[] =>
  arr(raw).flatMap((o) => {
    const out = outcome(o);
    return out ? [out] : [];
  });

function amount(raw: unknown): MarketAmount | undefined {
  const a = obj(raw);
  const display = str(a.amount_display);
  if (!display) return undefined;
  return { producer_id: str(a.producer_id), amount_cents: num(a.amount_cents), amount_display: display, paid: bool(a.paid) };
}

const amounts = (raw: unknown): MarketAmount[] =>
  arr(raw).flatMap((p) => {
    const a = amount(p);
    return a ? [a] : [];
  });

const REASONS: readonly BackReason[] = ["anonymous", "closed", "unverified", "entrant"];
const STATUSES: readonly Market["status"][] = ["open", "locked", "settled", "voided"];

// A market read, or null when there is no market (or the read is unusable):
// the page then renders exactly as it would with no market at all.
export function normalizeMarket(raw: unknown): Market | null {
  const m = obj(raw);
  if (m.has_market !== true || !str(m.market_id) || !STATUSES.includes(m.status as Market["status"])) return null;
  const v = obj(m.viewer);
  const s = obj(m.settlement);
  return {
    market_id: m.market_id as string,
    status: m.status as Market["status"],
    tier: typeof m.tier === "string" ? asTier(m.tier) : undefined,
    pool_cents: num(m.pool_cents),
    pool_display: str(m.pool_display) ?? "",
    take_bps: num(m.take_bps),
    rake_bps: num(m.rake_bps),
    producer_bps: num(m.producer_bps),
    backing_bps: num(m.backing_bps),
    fee_display: str(m.fee_display) ?? "",
    producer_cut_display: str(m.producer_cut_display) ?? "",
    backing_display: str(m.backing_display),
    min_stake_display: str(m.min_stake_display) ?? "",
    per_user_cap_display: str(m.per_user_cap_display) ?? "",
    bonus_display: str(m.bonus_display),
    house_display: str(m.house_display),
    outcomes: normalizeOutcomes(m.outcomes),
    settlement: str(s.pool_display)
      ? {
          pool_display: s.pool_display as string,
          rake_display: str(s.rake_display) ?? "",
          producer_cut_display: str(s.producer_cut_display) ?? "",
          winner_producer_id: str(s.winner_producer_id),
          winner_username: str(s.winner_username),
        }
      : undefined,
    void_reason: str(m.void_reason),
    viewer: {
      can_back: bool(v.can_back),
      reason: REASONS.includes(v.reason as BackReason) ? (v.reason as BackReason) : undefined,
      balance_cents: typeof v.balance_cents === "number" ? v.balance_cents : undefined,
      balance_display: str(v.balance_display),
      stake_cents: num(v.stake_cents),
      picks: amounts(v.picks),
      payout: amount(v.payout),
      refund: amount(v.refund),
      backing: backing(v.backing),
    },
  };
}

// backing keeps the viewer's backing share only when the server sent a whole
// one: a display string and at least one backer.
function backing(raw: unknown): Market["viewer"]["backing"] {
  const b = obj(raw);
  const display = str(b.earned_display);
  const backers = num(b.backers);
  if (!display || backers < 1) return undefined;
  return { earned_cents: num(b.earned_cents), earned_display: display, backers };
}

// withPick applies a POST /v1/web/market/pick response to the market read.
// Every figure comes from the response; nothing is recomputed here.
export function withPick(market: Market, raw: unknown): Market {
  const b = obj(raw);
  return {
    ...market,
    pool_cents: typeof b.pool_cents === "number" ? b.pool_cents : market.pool_cents,
    pool_display: str(b.pool_display) ?? market.pool_display,
    outcomes: Array.isArray(b.outcomes) ? normalizeOutcomes(b.outcomes) : market.outcomes,
    viewer: {
      ...market.viewer,
      balance_display: str(b.balance_display) ?? market.viewer.balance_display,
      stake_cents: typeof b.stake_cents === "number" ? b.stake_cents : market.viewer.stake_cents,
      picks: Array.isArray(b.picks) ? amounts(b.picks) : market.viewer.picks,
    },
  };
}

export const contestURL = (slug: string, tail = "") => `/v1/web/contests/${encodeURIComponent(slug)}${tail}`;

function getJSON(url: string, signal: AbortSignal): Promise<Response> {
  return fetch(url, { method: "GET", credentials: "same-origin", signal, headers: { Accept: "application/json" } });
}

export type Reply = { ok: boolean; status: number; body: Raw };

// postJSON never throws: a network failure is status 0, and every error body
// carries a reason the page shows as text.
export async function postJSON(url: string, body: unknown): Promise<Reply> {
  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
    const parsed = obj(await res.json().catch(() => ({})));
    return { ok: res.ok, status: res.status, body: parsed };
  } catch {
    return { ok: false, status: 0, body: {} };
  }
}

export const reasonOf = (reply: Reply): string | undefined => str(reply.body.reason);

export type DetailState =
  | { status: "loading" }
  | { status: "notfound" }
  | { status: "error" }
  | { status: "ready"; contest: ContestDetail };

export function useContestDetail(slug: string) {
  const [state, setState] = useState<DetailState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: "loading" });
    getJSON(contestURL(slug), ctrl.signal)
      .then(async (res) => {
        if (res.status === 404) return setState({ status: "notfound" });
        if (!res.ok) throw new Error(`contest ${res.status}`);
        const contest = normalizeDetail(await res.json());
        setState(contest ? { status: "ready", contest } : { status: "error" });
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setState({ status: "error" });
      });
    return () => ctrl.abort();
  }, [slug, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  // The Join tab replaces the viewer block with the one an action returns.
  const setViewer = useCallback(
    (viewer: ContestViewer) =>
      setState((cur) => (cur.status === "ready" ? { status: "ready", contest: { ...cur.contest, viewer } } : cur)),
    [],
  );
  return { state, retry, setViewer };
}

// A list read for a tab. null until it loads, and null if it fails: either
// way the tab stays hidden rather than showing a placeholder.
export function useContestList<T>(url: string | null, normalize: (raw: unknown) => T[]): T[] | null {
  const [list, setList] = useState<T[] | null>(null);
  useEffect(() => {
    setList(null);
    if (!url) return;
    const ctrl = new AbortController();
    getJSON(url, ctrl.signal)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`list ${res.status}`))))
      .then((raw) => setList(normalize(raw)))
      .catch(() => undefined);
    return () => ctrl.abort();
  }, [url, normalize]);
  return list;
}

export function useMarket(slug: string | null) {
  const [market, setMarket] = useState<Market | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setMarket(null);
    setLoaded(false);
    if (!slug) return;
    const ctrl = new AbortController();
    getJSON(contestURL(slug, "/market"), ctrl.signal)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`market ${res.status}`))))
      .then((raw) => setMarket(normalizeMarket(raw)))
      .catch(() => undefined)
      .finally(() => {
        if (!ctrl.signal.aborted) setLoaded(true);
      });
    return () => ctrl.abort();
  }, [slug]);
  return { market, loaded, setMarket };
}
